import { useCallback, useMemo, useEffect } from 'react';
import {
  setPeriodStart,
  setPeriodEnd,
  selectMeteorologyAgroCultureIndexLocationPeriodStart,
  selectMeteorologyAgroCultureIndexLocationPeriodEnd,
} from 'redux/reducers/meteorologyAgroCultureIndexLocationReducer';
import {
  format,
  parse,
} from 'date-fns';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import { useLazyGetCultureIndexForecastPeriodQuery } from 'services/api/Meteorology/Agro/CultureIndex';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import useForecastDate from './useForecastDate';

export default function useForecastPeriod() {
  const { forecastDate } = useForecastDate();
  const dateString = forecastDate ? format(forecastDate, 'yyyy-MM-dd') : null;

  const [trigger, {
    data, isFetching, error,
  }] = useLazyGetCultureIndexForecastPeriodQuery();

  useHandleRequestErrors(error as ResponseError);

  useEffect(() => {
    if (dateString) trigger({ forecastDate: dateString });
  }, [dateString, trigger]);

  const availablePeriod = useMemo(() => {
    if (!data) return [];
    return data.map((date) => parse(date, 'yyyy-MM-dd', new Date()));
  }, [data]);

  const changePeriod = useCallback((newDates: (Date | null)[]) => {
    const [newStartDate, newEndDate] = newDates;
    const newPeriodStart = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : undefined;
    const newPeriodEnd = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStart(newPeriodStart));
    store.dispatch(setPeriodEnd(newPeriodEnd));
  }, []);

  const start = useSelector(selectMeteorologyAgroCultureIndexLocationPeriodStart);
  const periodStart = start ? parse(start, 'yyyy-MM-dd', new Date()) : null;
  const end = useSelector(selectMeteorologyAgroCultureIndexLocationPeriodEnd);
  const periodEnd = end ? parse(end, 'yyyy-MM-dd', new Date()) : null;

  return {
    availablePeriod,
    changePeriod,
    periodStart,
    periodEnd,
    isFetching,
  };
}
